import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2, AlertTriangle, Plus, Minus, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * SessionAccountingCard — the "attendance ↔ debits" reconciliation card
 * for a single athlete on the coach dossier.
 *
 * Sachin 2026-02-28: "Some peoples attendance and session count don't
 * match up. That is not good. We should flag that and have ways to
 * manually adjust if needed."
 *
 * Shows:
 *   • Attended count (from bookings.status=checked_in)
 *   • Debited count (session_debits — pack/sub/comp/manual)
 *   • Open IOUs (unsettled debit rows)
 *   • Delta — if non-zero, the card highlights in amber
 *
 * Actions:
 *   • + Add debit (manual +1)  — for a missed check-in you never logged
 *   • − Credit back (manual -1) — for a double-charge you want refunded
 *   • Refresh
 */
export default function SessionAccountingCard({ athleteId, athleteName }) {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const r = await axios.get(
                `${API}/coach/athletes/${athleteId}/session-accounting`,
                { withCredentials: true },
            );
            setData(r.data);
        } catch (e) {
            toast.error("Couldn't load session accounting", {
                description: e?.response?.data?.detail || "Try again.",
            });
        }
    }, [athleteId]);

    useEffect(() => { load(); }, [load]);

    const adjust = async (delta) => {
        const label = delta > 0 ? "add a debit (mark 1 session as used)" : "credit 1 session back";
        const reason = window.prompt(`Reason to ${label}? (appears in audit log)`, "") ?? "";
        setBusy(true);
        try {
            await axios.post(
                `${API}/coach/athletes/${athleteId}/session-debit-manual`,
                { delta, reason: reason.trim() || null },
                { withCredentials: true },
            );
            toast.success(delta > 0 ? "Session debited" : "Session credited back");
            await load();
        } catch (e) {
            toast.error("Adjustment failed", {
                description: e?.response?.data?.detail || "Try again.",
            });
        } finally {
            setBusy(false);
        }
    };

    const autoHeal = async () => {
        setBusy(true);
        try {
            const r = await axios.post(
                `${API}/coach/session-debits/heal`,
                {}, { withCredentials: true },
            );
            const { backfilled = 0, burned = 0, orphans = 0 } = r.data || {};
            if (backfilled || burned) {
                toast.success(`Reconciled ${backfilled + burned} session${backfilled + burned === 1 ? "" : "s"}`, {
                    description: `${backfilled} matched to existing deductions · ${burned} newly debited.`,
                });
            } else {
                toast.info(orphans === 0
                    ? "No missing debits found — any remaining drift needs a manual adjust below."
                    : "Ran, but nothing could be auto-matched. Use manual adjust.");
            }
            await load();
        } catch (e) {
            toast.error("Auto-reconcile failed", {
                description: e?.response?.data?.detail || "Try again.",
            });
        } finally {
            setBusy(false);
        }
    };

    if (!data) {
        return (
            <div className="border border-zinc-800 rounded-2xl p-5 bg-zinc-950/60">
                <div className="flex items-center gap-2 text-zinc-500 text-xs">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading accounting…
                </div>
            </div>
        );
    }

    const healthy = data.is_healthy;
    const delta = data.delta;

    return (
        <div
            data-testid="session-accounting-card"
            className={`border rounded-2xl p-5 mb-6 ${
                healthy
                    ? "border-zinc-800 bg-zinc-950/60"
                    : "border-amber-800/60 bg-amber-950/20"
            }`}
        >
            <div className="flex items-start justify-between gap-3 mb-4">
                <div>
                    <div className="text-[10px] tracking-[0.25em] uppercase text-brand-red mb-1">
                        Session accounting
                    </div>
                    <h3 className="font-display text-lg tracking-wider text-white">
                        {athleteName ? `${athleteName}` : "This athlete"} — attendance vs debits
                    </h3>
                    <p className="text-[11px] text-zinc-500 mt-0.5 max-w-xl">
                        Every check-in should produce one debit row. If not, adjust below.
                    </p>
                </div>
                <button
                    type="button"
                    data-testid="session-accounting-refresh"
                    onClick={load}
                    disabled={busy}
                    className="p-2 border border-zinc-800 text-zinc-400 hover:border-zinc-600 hover:text-zinc-200 rounded-lg"
                    title="Refresh"
                >
                    <RefreshCw className="w-3.5 h-3.5" />
                </button>
            </div>

            {/* Numbers */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                <NumTile label="Attended" value={data.attended_count} testid="acct-attended" />
                <NumTile label="Debited" value={data.debited_count} testid="acct-debited" />
                <NumTile
                    label="Open IOUs"
                    value={data.open_iou_count}
                    tone={data.open_iou_count > 0 ? "amber" : "default"}
                    testid="acct-ious"
                />
                <NumTile
                    label="Delta"
                    value={delta > 0 ? `+${delta}` : `${delta}`}
                    tone={healthy ? "green" : "amber"}
                    testid="acct-delta"
                />
            </div>

            {/* Health strip */}
            <div
                data-testid={`accounting-status-${healthy ? "healthy" : "drift"}`}
                className={`flex items-center gap-2 text-xs mb-4 px-3 py-2 rounded-lg ${
                    healthy
                        ? "text-emerald-300 bg-emerald-950/30 border border-emerald-800/40"
                        : "text-amber-300 bg-amber-950/40 border border-amber-800/40"
                }`}
            >
                {healthy ? (
                    <>
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        Attendance and debits match. Ledger is clean.
                    </>
                ) : (
                    <>
                        <AlertTriangle className="w-3.5 h-3.5" />
                        {delta > 0
                            ? `Attended ${delta} more session${delta === 1 ? "" : "s"} than debited. Consider adding a manual debit.`
                            : `Debited ${Math.abs(delta)} more session${Math.abs(delta) === 1 ? "" : "s"} than attended. Consider crediting one back.`}
                    </>
                )}
            </div>

            {/* Adjust buttons — always visible, always work, even without a pack. */}
            <div className="flex items-center gap-2 flex-wrap">
                {!healthy && (
                    <button
                        type="button"
                        data-testid="acct-auto-heal"
                        onClick={autoHeal}
                        disabled={busy}
                        className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase border border-amber-700/60 bg-amber-900/20 text-amber-200 hover:bg-amber-800/40 px-3 py-1.5 disabled:opacity-40"
                        title="Scans every attended session missing a debit and repairs it automatically (matches existing deductions first, debits only true gaps)."
                    >
                        <RefreshCw className="w-3 h-3" />
                        Auto-reconcile
                    </button>
                )}
                <button
                    type="button"
                    data-testid="acct-adjust-plus"
                    onClick={() => adjust(1)}
                    disabled={busy}
                    className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase border border-zinc-700 text-zinc-200 hover:border-rose-500 hover:text-white px-3 py-1.5 disabled:opacity-40"
                    title="Add a debit — mark 1 session as used"
                >
                    <Plus className="w-3 h-3" />
                    Add debit
                </button>
                <button
                    type="button"
                    data-testid="acct-adjust-minus"
                    onClick={() => adjust(-1)}
                    disabled={busy}
                    className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase border border-zinc-700 text-zinc-200 hover:border-emerald-500 hover:text-white px-3 py-1.5 disabled:opacity-40"
                    title="Credit 1 session back — for double-charges"
                >
                    <Minus className="w-3 h-3" />
                    Credit back
                </button>
                {busy && <Loader2 className="w-3.5 h-3.5 animate-spin text-zinc-500" />}
            </div>

            {/* Last few debits, at-a-glance. */}
            {data.recent_debits && data.recent_debits.length > 0 && (
                <details className="mt-4 text-xs">
                    <summary className="cursor-pointer text-zinc-500 hover:text-zinc-300">
                        Recent debits ({data.recent_debits.length})
                    </summary>
                    <div className="mt-2 space-y-1">
                        {data.recent_debits.map((d) => (
                            <div key={d.debit_id} className="flex items-center gap-2 text-[11px] text-zinc-400 border-t border-zinc-900 pt-1">
                                <span className={
                                    d.kind === "iou" ? "text-amber-400"
                                    : d.kind === "manual" ? "text-sky-400"
                                    : d.kind === "comp" ? "text-emerald-400"
                                    : "text-zinc-500"
                                }>
                                    {d.kind}
                                </span>
                                <span className="text-zinc-500 flex-1 truncate">
                                    {d.occurred_at?.slice(0, 10)}
                                </span>
                                {d.delta && (
                                    <span className={d.delta > 0 ? "text-rose-400" : "text-emerald-400"}>
                                        {d.delta > 0 ? "+" : ""}{d.delta}
                                    </span>
                                )}
                                {d.reason && (
                                    <span className="text-zinc-500 truncate max-w-[40%]" title={d.reason}>
                                        · {d.reason}
                                    </span>
                                )}
                            </div>
                        ))}
                    </div>
                </details>
            )}
        </div>
    );
}

function NumTile({ label, value, tone = "default", testid }) {
    const cls = {
        default: "text-zinc-200 border-zinc-800",
        amber: "text-amber-300 border-amber-800/60",
        green: "text-emerald-300 border-emerald-800/60",
    }[tone];
    return (
        <div className={`border rounded-lg p-3 bg-black/40 ${cls}`}>
            <div className="text-[9px] tracking-[0.25em] uppercase text-zinc-500 mb-1">
                {label}
            </div>
            <div className="font-display text-2xl" data-testid={testid}>
                {value}
            </div>
        </div>
    );
}
